import { useState, useEffect, useRef } from "react";
import { useAuth } from "../App";
import usePushNotifications from "../hooks/usePushNotifications";
import { Bell, X } from "lucide-react";

const DISMISS_KEY = "push_banner_dismissed_at";
const DISMISS_DAYS = 3; // volver a mostrar después de N días si sigue sin activar
const AUTO_PROMPT_KEY = "push_auto_prompted"; // 1 intento de auto-solicitud por sesión
const MIN_VISIBLE_MS = 60000; // el banner permanece visible al menos 60s una vez mostrado

/**
 * Banner global que invita al usuario a activar las notificaciones de
 * escritorio (Web Push). Además, AUTO-SOLICITA el permiso apenas el usuario
 * inicia sesión (en navegadores que lo permiten, como Chrome/Edge): aparece
 * el cuadro nativo "Permitir notificaciones" sin que el usuario tenga que
 * pulsar el botón "Activar". Si el navegador requiere un gesto (ej. Safari)
 * o el usuario lo descarta, el banner queda como respaldo.
 *
 * Para que el usuario alcance a pulsar "Activar", una vez que el banner se
 * muestra permanece visible AL MENOS 60 segundos, aunque el estado de
 * suscripción/permiso cambie de forma transitoria (salvo que el usuario lo
 * descarte o active manualmente).
 */
export default function PushPermissionBanner() {
  const { user, token } = useAuth();
  const { isSupported, permission, isSubscribed, loading, subscribe } = usePushNotifications(token);
  const [dismissed, setDismissed] = useState(false);
  const autoPromptedRef = useRef(false);
  const resubscribedRef = useRef(false);
  const firstShownRef = useRef(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (ts) {
      const diasPasados = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
      if (diasPasados < DISMISS_DAYS) setDismissed(true);
    }
  }, []);

  // Auto-solicitud del permiso al iniciar sesión (1 vez por sesión).
  useEffect(() => {
    if (!user || !token || !isSupported) return;
    if (permission !== "default") return; // ya concedido o denegado -> no insistir
    if (isSubscribed) return;
    if (autoPromptedRef.current) return;
    if (sessionStorage.getItem(AUTO_PROMPT_KEY)) return;
    autoPromptedRef.current = true;
    sessionStorage.setItem(AUTO_PROMPT_KEY, "1");
    // Pequeño retraso para asegurar que el service worker esté listo.
    const t = setTimeout(() => {
      subscribe();
    }, 2500);
    return () => clearTimeout(t);
  }, [user, token, isSupported, permission, isSubscribed, subscribe]);

  // Re-suscripción SILENCIOSA: si el permiso YA fue concedido pero se perdió la
  // suscripción (p.ej. tras un redespliegue o luego de un rato de inactividad,
  // el navegador/servicio push la puede descartar), la volvemos a crear en
  // segundo plano SIN volver a pedirle al usuario que la "Active". Como el
  // permiso ya está en "granted", subscribe() no muestra ningún cuadro nativo.
  useEffect(() => {
    if (!user || !token || !isSupported) return;
    if (permission !== "granted") return;
    if (isSubscribed) return;
    if (resubscribedRef.current) return;
    const t = setTimeout(() => {
      resubscribedRef.current = true;
      subscribe();
    }, 1500);
    return () => clearTimeout(t);
  }, [user, token, isSupported, permission, isSubscribed, subscribe]);

  // Elegibilidad base: navegador soportado, sesión activa, no denegado y no ya suscrito.
  const baseEligible =
    !!user && !!token && isSupported &&
    permission !== "denied" && !(isSubscribed && permission === "granted") &&
    !dismissed;

  // Al volverse elegible por primera vez, marcar el momento y programar un
  // re-render a los 60s para permitir ocultarlo después si ya no hace falta.
  useEffect(() => {
    if (baseEligible && firstShownRef.current === null) {
      firstShownRef.current = Date.now();
      const t = setTimeout(() => forceTick((x) => x + 1), MIN_VISIBLE_MS);
      return () => clearTimeout(t);
    }
  }, [baseEligible]);

  // No mostrar nunca si: sin usuario/soporte, permiso denegado o descartado.
  if (!user || !token || !isSupported) return null;
  if (permission === "denied") return null;
  // Si el permiso YA fue concedido, nunca mostramos el banner: si faltara la
  // suscripción se re-crea en segundo plano (efecto de arriba) sin molestar al
  // usuario. Esto evita que se le vuelva a pedir "Activar" tras un redespliegue.
  if (permission === "granted") return null;
  if (dismissed) return null;

  // A partir de aquí el usuario AÚN NO está activado. Garantizamos que el
  // banner permanezca visible al menos 60s (ventana mínima) para que alcance a
  // pulsar "Activar", aunque el estado cambie de forma transitoria.
  const withinMinWindow =
    firstShownRef.current !== null && Date.now() - firstShownRef.current < MIN_VISIBLE_MS;

  // Ocultar solo si ya no es elegible Y pasó la ventana mínima de 60s.
  if (!baseEligible && !withinMinWindow) return null;
  // Si nunca llegó a ser elegible, no mostrar.
  if (firstShownRef.current === null) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleActivar = async () => {
    const ok = await subscribe();
    if (ok) setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-xl"
      data-testid="push-permission-banner"
    >
      <div className="flex items-center gap-3 rounded-xl border border-indigo-200 bg-white/95 backdrop-blur shadow-lg px-4 py-3 dark:bg-slate-900/95 dark:border-indigo-800">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40">
          <Bell className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Activa las notificaciones de escritorio
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Recibe avisos importantes sobre tus programas, aunque tengas el navegador minimizado.
          </p>
        </div>
        <button
          onClick={handleActivar}
          disabled={loading}
          data-testid="push-banner-activate-btn"
          className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading ? "Activando…" : "Activar"}
        </button>
        <button
          onClick={handleDismiss}
          data-testid="push-banner-dismiss-btn"
          className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
